import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaAtom, FaCheck, FaEnvelope, FaArrowRight, FaSearch, FaUniversity, FaMobileAlt, FaIdCard } from 'react-icons/fa';
import {
  FiUser,
  FiUsers,
  FiAward,
  FiBriefcase,
  FiStar,
  FiGlobe,
} from 'react-icons/fi';
import {
  MdHowToVote,
  MdSchool,
  MdPayment,
} from 'react-icons/md';
import './Membership.css';
import { searchMembers } from '../../services/memberService';

/* ── Membership Tiers Data ── */
const TIERS = [
  {
    id:          'student',
    tier:        'Tier 1',
    title:       'Student Member',
    icon:        <MdSchool size={28} style={{ color: '#4d0a0a' }} />,
    description: 'For undergraduate and postgraduate students pursuing nuclear-related studies.',
    price:       'KES 500',
    period:      '/ year',
    popular:     false,
    honorary:    false,
    benefits: [
      'Access to all NSK events and seminars',
      'Student-focused newsletter',
      'Mentorship from senior members',
      'Discounted conference registration',
      'Access to NSK student network',
    ],
    ctaLabel: 'Apply Now',
    ctaStyle: 'primary',
  },
  {
    id:          'associate',
    tier:        'Tier 2',
    title:       'Associate Member',
    icon:        <FiUser size={28} style={{ color: '#727a1a' }} />,
    description: 'For early-career professionals with up to 5 years of experience in nuclear fields.',
    price:       'KES 2,000',
    period:      '/ year',
    popular:     false,
    honorary:    false,
    benefits: [
      'All Student Member benefits',
      'Voting rights at AGM',
      'Technical workshops access',
      'NSK professional directory listing',
      'Career development resources',
    ],
    ctaLabel: 'Apply Now',
    ctaStyle: 'primary',
  },
  {
    id:          'regular',
    tier:        'Tier 3',
    title:       'Regular Member',
    icon:        <FiUsers size={28} style={{ color: '#4cbcc9' }} />,
    description: 'For qualified nuclear professionals. The core membership category of NSK.',
    price:       'KES 5,000',
    period:      '/ year',
    popular:     true,
    honorary:    false,
    benefits: [
      'All Associate Member benefits',
      'Eligibility to stand for election',
      'IAEA publications and reports',
      'Sub-committee participation',
      'International conference representation',
    ],
    ctaLabel: 'Apply Now',
    ctaStyle: 'gold',
  },
  {
    id:          'affiliate',
    tier:        'Tier 4',
    title:       'Affiliate Member',
    icon:        <FiGlobe size={28} style={{ color: '#110a4d' }} />,
    description: 'For professionals from related fields with a strong interest in nuclear science.',
    price:       'KES 3,000',
    period:      '/ year',
    popular:     false,
    honorary:    false,
    benefits: [
      'Access to NSK events and seminars',
      'NSK newsletter subscription',
      'Networking with nuclear professionals',
      'Technical publications access',
      'Interdisciplinary collaboration opportunities',
    ],
    ctaLabel: 'Apply Now',
    ctaStyle: 'primary',
  },
  {
    id:          'corporate',
    tier:        'Tier 5',
    title:       'Corporate Member',
    icon:        <FiBriefcase size={28} style={{ color: '#0a4a4d' }} />,
    description: 'For organisations operating in or supporting the nuclear sector in Kenya.',
    price:       'KES 50,000',
    period:      '/ year',
    popular:     false,
    honorary:    false,
    benefits: [
      '5 staff Regular memberships included',
      'Logo on NSK website and materials',
      'Sponsorship recognition at events',
      'Priority speaking slots at conferences',
      'Direct engagement with NSK leadership',
    ],
    ctaLabel: 'Apply Now',
    ctaStyle: 'primary',
  },
  {
    id:          'honorary',
    tier:        'Tier 6',
    title:       'Honorary Member',
    icon:        <FiAward size={28} style={{ color: '#C9A84C' }} />,
    description: 'Awarded to distinguished individuals who have made exceptional contributions to nuclear science.',
    price:       null,
    period:      null,
    popular:     false,
    honorary:    true,
    benefits: [
      'Lifetime NSK membership',
      'Recognition at NSK Annual Dinner',
      'Advisory role to the Executive Committee',
      'Listed in NSK Hall of Honour',
      'Complimentary conference access',
    ],
    ctaLabel: 'Contact Us',
    ctaStyle: 'outline',
  },
];

/* ── Application Process Steps ── */
const STEPS = [
  {
    number: '01',
    icon:   <FiStar size={22} style={{ color: '#c94c4c' }} />,
    title:  'Choose Your Tier',
    text:   'Select the membership category that best fits your qualifications and career stage.',
  },
  {
    number: '02',
    icon:   <FaEnvelope size={22} style={{ color: '#4e4cc9' }} />,
    title:  'Submit Application',
    text:   'Click Apply Now and send your application email with your CV and relevant credentials.',
  },
  {
    number: '03',
    icon:   <MdPayment size={22} style={{ color: '#5fc94c' }} />,
    title:  'Pay Membership Fee',
    text:   'Complete your annual membership fee payment via M-Pesa or bank transfer.',
  },
  {
    number: '04',
    icon:   <MdHowToVote size={22} style={{ color: '#bc4cc9' }} />,
    title:  'Welcome to NSK',
    text:   'Receive your membership certificate and get access to all NSK member benefits.',
  },
];


/* ── Tier Card Component ── */
function TierCard({ tier }) {
  const {
    id,
    tier: tierLabel,
    title,
    icon,
    description,
    price,
    period,
    popular,
    honorary,
    benefits,
    ctaLabel,
    ctaStyle,
  } = tier;

  return (
    <article
      className={[
        'tier-card',
        popular  ? 'tier-card--popular'  : '',
        honorary ? 'tier-card--honorary' : '',
      ].join(' ').trim()}
      aria-label={`${title} membership tier`}
    >
      {popular && (
        <div className="tier-card__badge" aria-label="Most popular tier">
          Most Popular
        </div>
      )}

      <div className="tier-card__header">
        <span className="tier-card__tier-label">{tierLabel}</span>
        <div className="tier-card__icon-wrapper" aria-hidden="true">
          {icon}
        </div>
        <h3 className="tier-card__title">{title}</h3>
        <p className="tier-card__description">{description}</p>
      </div>

      {honorary ? (
        <div className="tier-card__price--invitation">By Invitation</div>
      ) : (
        <div className="tier-card__price">
          <span className="tier-card__price-amount">{price}</span>
          <span className="tier-card__price-period">{period}</span>
        </div>
      )}

      <ul className="tier-card__benefits" aria-label={`${title} benefits`}>
        {benefits.map((benefit) => (
          <li className="tier-card__benefit" key={benefit}>
            <FaCheck
              size={13}
              style={{ color: '#1A7A47', marginTop: '2px', flexShrink: 0 }}
              aria-hidden="true"
            />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/join?tier=${id}`}
        className={`tier-card__cta tier-card__cta--${ctaStyle}`}
        aria-label={`${ctaLabel} for ${title}`}
      >
        {ctaLabel}
        <FaArrowRight
          size={13}
          aria-hidden="true"
          style={{ marginLeft: '0.4rem' }}
        />
      </Link>
    </article>
  );
}

/* ── Member Verification Component ── */
function MemberVerification() {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  async function handleSearch(e) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      const found = await searchMembers(q);
      setResults(
        found.map((m) => ({
          name:   `${m.firstName} ${m.secondName || m.lastName || ''}`.trim(),
          number: m.membershipNumber,
          tier:   m.tier,
          status: m.status,
        }))
      );
    } catch (err) {
      console.error('Member search failed:', err);
      setResults([]);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  }

  function handleClear() {
    setQuery('');
    setResults(null);
    setSearched(false);
    inputRef.current?.focus();
  }

  return (
    <section className="verify" aria-labelledby="verify-heading">
      <div className="verify__inner">
        <div className="verify__header">
          <p className="section__label">Member Directory</p>
          <h2 className="section__title" id="verify-heading">
            Verify NSK Membership
          </h2>
          <p className="verify__subtitle">
            Search by name or membership number to confirm that an
            individual is a registered member of the Nuclear Society
            of Kenya.
          </p>
        </div>

        <div className="verify__card">
          <form
            className="verify__form"
            onSubmit={handleSearch}
            aria-label="Member verification search"
          >
            <div className="verify__input-wrap">
              <FaSearch
                className="verify__input-icon"
                size={16}
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="search"
                className="verify__input"
                placeholder="Enter name or membership no. e.g. David Otwoma or NSK-050"
                value={query}
                onChange={e => setQuery(e.target.value)}
                aria-label="Search member by name or membership number"
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              className="verify__btn"
              disabled={!query.trim() || loading}
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </form>

          {/* Results */}
          {searched && (
            <div
              className="verify__results"
              role="region"
              aria-live="polite"
              aria-label="Search results"
            >
              {results.length > 0 ? (
                <>
                  <p className="verify__results-count">
                    {results.length} member{results.length !== 1 ? 's' : ''} found
                  </p>
                  <ul className="verify__results-list">
                    {results.map(member => (
                      <li
                        key={member.number}
                        className="verify__result-item"
                      >
                        <div className="verify__result-avatar" aria-hidden="true">
                          <FaIdCard size={18} />
                        </div>
                        <div className="verify__result-info">
                          <span className="verify__result-name">
                            {member.name}
                          </span>
                          <span className="verify__result-number">
                            Membership No: {member.number}
                          </span>
                          {member.tier && (
                            <span className="verify__result-tier">
                              {member.tier}
                            </span>
                          )}
                        </div>
                        <div
                          className={[
                            'verify__result-badge',
                            member.status && member.status !== 'Active'
                              ? 'verify__result-badge--inactive'
                              : '',
                          ].join(' ').trim()}
                        >
                          <FaCheck size={11} style={{ marginRight: '4px' }} />
                          {member.status || 'Verified'}
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="verify__no-results">
                  <p className="verify__no-results-title">No member found</p>
                  <p className="verify__no-results-text">
                    No NSK member matches "<strong>{query}</strong>".
                    Please check the spelling/number or contact us at{' '}
                    <a href="mailto:info@nuclearsocietyofkenya.org">
                      info@nuclearsocietyofkenya.org
                    </a>{' '}
                    to verify manually.
                  </p>
                </div>
              )}

              <button
                className="verify__clear"
                onClick={handleClear}
                type="button"
              >
                Clear search
              </button>
            </div>
          )}

          <p className="verify__note">
            Only active NSK members are listed. For disputes or corrections,
            contact{' '}
            <a href="mailto:info@nuclearsocietyofkenya.org" className="verify__note-link">
              info@nuclearsocietyofkenya.org
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ── Payment Details Component ── */
function PaymentDetails() {
  return (
    <section className="payment" aria-labelledby="payment-heading">
      <div className="payment__inner">
        <div className="payment__header">
          <p className="section__label">Payment Information</p>
          <h2 className="section__title" id="payment-heading">
            How to Pay Your Membership Fee
          </h2>
          <p className="payment__subtitle">
            Once your application is approved, complete your payment using
            any of the options below. Use your full name as the reference.
          </p>
        </div>

        <div className="payment__grid">

          {/* ── M-Pesa Paybill ── */}
          <div className="payment__card">
            <div className="payment__card-header">
              <div className="payment__card-icon payment__card-icon--mpesa">
                <FaMobileAlt size={24} style={{ color: '#fff' }} />
              </div>
              <div>
                <h3 className="payment__card-title">M-Pesa Paybill</h3>
                <p className="payment__card-subtitle">Recommended — fastest method</p>
              </div>
            </div>

            <div className="payment__how-steps">
              <div className="payment__how-step">
                <div className="payment__how-step-num">1</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Open M-Pesa</p>
                  <p className="payment__how-step-text">
                    Go to <strong>M-Pesa</strong> menu on your phone
                  </p>
                </div>
              </div>
              <div className="payment__how-step">
                <div className="payment__how-step-num">2</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Select Lipa na M-Pesa</p>
                  <p className="payment__how-step-text">
                    Choose <strong>Lipa na M-Pesa</strong> then <strong>Pay Bill</strong>
                  </p>
                </div>
              </div>
              <div className="payment__how-step">
                <div className="payment__how-step-num">3</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Enter Business Number</p>
                  <div className="payment__highlight-box">
                    <span className="payment__highlight-label">Paybill Number</span>
                    <span className="payment__highlight-value">522533</span>
                  </div>
                </div>
              </div>
              <div className="payment__how-step">
                <div className="payment__how-step-num">4</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Enter Account Number</p>
                  <div className="payment__highlight-box">
                    <span className="payment__highlight-label">Account Number</span>
                    <span className="payment__highlight-value">123[YourName]</span>
                  </div>
                  <p className="payment__how-step-text" style={{ marginTop: '6px' }}>
                    e.g. if your name is John Doe, enter <strong>123JohnDoe</strong>
                  </p>
                </div>
              </div>
              <div className="payment__how-step">
                <div className="payment__how-step-num">5</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Enter Amount</p>
                  <p className="payment__how-step-text">
                    Enter the amount matching your membership tier (see fee table below)
                  </p>
                </div>
              </div>
              <div className="payment__how-step">
                <div className="payment__how-step-num">6</div>
                <div className="payment__how-step-body">
                  <p className="payment__how-step-title">Enter M-Pesa PIN & Confirm</p>
                  <p className="payment__how-step-text">
                    Enter your M-Pesa PIN and confirm. You will receive an
                    SMS confirmation with a receipt number.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Bank Transfer ── */}
          <div className="payment__card">
            <div className="payment__card-header">
              <div className="payment__card-icon payment__card-icon--bank">
                <FaUniversity size={24} style={{ color: '#fff' }} />
              </div>
              <div>
                <h3 className="payment__card-title">Bank Transfer</h3>
                <p className="payment__card-subtitle">Recommended for corporate members</p>
              </div>
            </div>

            <div className="payment__bank-details">
              {[
                { label: 'Bank Name',      value: 'Equity Bank Kenya'        },
                { label: 'Account Name',   value: 'Nuclear Society of Kenya' },
                { label: 'Account Number', value: '0123456789'               },
                { label: 'Branch',         value: 'Nairobi Upper Hill'       },
                { label: 'Swift Code',     value: 'EQBLKENA'                 },
              ].map(({ label, value }) => (
                <div className="payment__bank-row" key={label}>
                  <span className="payment__bank-label">{label}</span>
                  <span className="payment__bank-value">{value}</span>
                </div>
              ))}
            </div>

            <div className="payment__bank-ref">
              <p className="payment__bank-ref-title">Payment Reference Format</p>
              <div className="payment__highlight-box">
                <span className="payment__highlight-label">Reference</span>
                <span className="payment__highlight-value">
                  NSK-[FullName]-[Tier]
                </span>
              </div>
              <p className="payment__how-step-text" style={{ marginTop: '8px' }}>
                e.g. <strong>NSK-John Doe-Regular Member</strong>
              </p>
            </div>

            <div className="payment__bank-note-box">
              <p>
                After payment, email your proof of payment to{' '}
                <a href="mailto:info@nuclearsocietyofkenya.org">
                  info@nuclearsocietyofkenya.org
                </a>{' '}
                with subject: <strong>"NSK Payment — [Your Name]"</strong>
              </p>
            </div>
          </div>
        </div>

        {/* ── Fee Reference Table ── */}
        <div className="payment__fee-table-wrap">
          <h3 className="payment__fee-title">Membership Fee Reference</h3>
          <table
            className="payment__fee-table"
            aria-label="Membership fees by tier"
          >
            <thead>
              <tr>
                <th scope="col">Tier</th>
                <th scope="col">Category</th>
                <th scope="col">Annual Fee</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.filter(t => !t.honorary).map(t => (
                <tr key={t.id}>
                  <td>{t.tier}</td>
                  <td>{t.title}</td>
                  <td className="payment__fee-amount">{t.price}</td>
                </tr>
              ))}
              <tr>
                <td>Tier 6</td>
                <td>Honorary Member</td>
                <td className="payment__fee-amount">By Invitation</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Online Payment — Coming Soon ── */}
        <div className="payment__online-card">
          <div className="payment__online-header">
            <div className="payment__online-badge">Coming Soon</div>
            <h3 className="payment__online-title">
              Pay Online via M-Pesa
            </h3>
            <p className="payment__online-subtitle">
              Online payment via M-Pesa STK Push is coming soon.
              You will enter your phone number, receive a prompt on
              your phone to enter your PIN, and get an instant receipt.
              For now, please use the Paybill details above.
            </p>
          </div>

          {/* Disabled form — ready for STK Push later */}
          <div className="payment__online-form" aria-label="Online payment form — coming soon">
            <div className="payment__online-form-row">
              <div className="payment__online-field">
                <label className="payment__online-label">
                  Full Name
                </label>
                <input
                  type="text"
                  className="payment__online-input"
                  placeholder="e.g. John Doe"
                  disabled
                  aria-disabled="true"
                />
              </div>
              <div className="payment__online-field">
                <label className="payment__online-label">
                  M-Pesa Phone Number
                </label>
                <input
                  type="tel"
                  className="payment__online-input"
                  placeholder="e.g. 0712 345 678"
                  disabled
                  aria-disabled="true"
                />
              </div>
            </div>
            <div className="payment__online-form-row">
              <div className="payment__online-field">
                <label className="payment__online-label">
                  Membership Tier
                </label>
                <select
                  className="payment__online-input"
                  disabled
                  aria-disabled="true"
                >
                  <option value="">Select tier</option>
                  {TIERS.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.title} — {t.price || 'By Invitation'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="payment__online-field">
                <label className="payment__online-label">
                  Amount (KES)
                </label>
                <input
                  type="text"
                  className="payment__online-input"
                  placeholder="Auto-filled by tier"
                  disabled
                  aria-disabled="true"
                />
              </div>
            </div>
            <button
              className="payment__online-btn"
              disabled
              aria-disabled="true"
            >
              Pay via M-Pesa — Coming Soon
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ══════════════════════════════════════════
   MEMBERSHIP PAGE
══════════════════════════════════════════ */
function Membership() {
  return (
    <>
      {/* ── Page Hero ── */}
      <section
        className="membership-hero"
        aria-labelledby="membership-hero-heading"
      >
        <div className="membership-hero__inner">
          <div className="membership-hero__icon" aria-hidden="true">
            <FaAtom size={48} style={{ color: '#C9A84C' }} />
          </div>
          <h1
            className="membership-hero__title"
            id="membership-hero-heading"
          >
            Become a Member of Kenya's Nuclear Community
          </h1>
          <p className="membership-hero__subtitle">
            Six membership categories designed for every stage of your
            nuclear career — from student to corporate partner. Join us
            in advancing peaceful nuclear science across Kenya and East Africa.
          </p>
        </div>
      </section>

      {/* ── Membership Tiers ── */}
      <section className="tiers" aria-labelledby="tiers-heading">
        <div className="tiers__inner">
          <div className="tiers__header">
            <p className="section__label">Membership Categories</p>
            <h2 className="section__title" id="tiers-heading">
              Choose Your Membership Tier
            </h2>
            <p className="tiers__note">
              All memberships run for one calendar year and are renewable annually.
            </p>
          </div>

          <div className="tiers__grid">
            {TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Application Process ── */}
      <section className="process" aria-labelledby="process-heading">
        <div className="process__inner">
          <div className="process__header">
            <p className="section__label">Application Process</p>
            <h2 className="section__title" id="process-heading">
              How to Join NSK
            </h2>
          </div>

          <ol className="process__steps">
            {STEPS.map(({ number, icon, title, text }) => (
              <li className="process__step" key={number}>
                <div className="process__step-number" aria-hidden="true">
                  {number}
                </div>
                <div className="process__step-icon" aria-hidden="true">
                  {icon}
                </div>
                <h3 className="process__step-title">{title}</h3>
                <p className="process__step-text">{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Payment Details ── */}
      <PaymentDetails />

      {/* ── Member Verification ── */}
      <MemberVerification />

      {/* ── CTA Banner ── */}
      <section
        className="membership-cta"
        aria-labelledby="membership-cta-heading"
      >
        <div className="membership-cta__inner">
          <h2
            className="membership-cta__title"
            id="membership-cta-heading"
          >
            Join Kenya's{' '}
            <span>Nuclear Community</span>{' '}
            Today
          </h2>
          <p className="membership-cta__text">
            Questions about membership? Email us at{' '}
            <a
              href="mailto:info@nuclearsocietyofkenya.org"
              className="membership-cta__email"
            >
              info@nuclearsocietyofkenya.org
            </a>
          </p>
          <Link
            to="/join"
            className="btn btn--primary"
            aria-label="Apply for NSK membership"
          >
            Apply for Membership
            <FaArrowRight
              size={14}
              aria-hidden="true"
              style={{ marginLeft: '0.5rem' }}
            />
          </Link>
        </div>
      </section>
    </>
  );
}

export default Membership;